import { useQuery } from "@tanstack/react-query";
import { Link, Outlet } from "@tanstack/react-router";
// The real Chirologo (white mark on the painted red square) — the group is a
// Chiro group, so the site carries the movement's mark, not an invented one.
import chiroLogo from "../assets/chiro-logo.png";
import { nl } from "../copy/nl.js";
import { fetchHealth } from "../lib/api.js";

const NAV = [
  { to: "/", label: nl.nav.kalender },
  { to: "/praktisch", label: nl.nav.praktisch },
  { to: "/aanvraag", label: nl.nav.aanvraag },
] as const;

/**
 * The demo disclaimer, driven by the API's DEMO_MODE flag — the site is
 * public and looks real, but every booking and price on it is fictional
 * (user call, 2026-08-24). Riding the flag means the go-live gate that
 * removes DEMO_MODE kills this banner in the same stroke: no one has to
 * remember it. Errors show nothing — a missing banner on a flaky connection
 * is better than a scary strip on a real site.
 */
function DemoBanner() {
  const health = useQuery({
    queryKey: ["health"],
    queryFn: fetchHealth,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
  if (health.data?.demo !== true) return null;
  return (
    <p className="bg-[#e3a92c] px-5 py-1.5 text-center font-semibold text-[12.5px] text-inkt">
      {nl.app.demoBanner}
    </p>
  );
}

/** Header, nav and footer around every public page. */
export function PublicLayout() {
  return (
    <div className="flex min-h-dvh flex-col">
      <DemoBanner />
      <header className="border-lijn border-b bg-kaart">
        {/* flex-wrap: the full organisation name is the brand and shows on
            every width — on a narrow phone the nav drops to its own row
            instead of squeezing the name out (user call, 2026-08-21). */}
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-x-6 gap-y-2 px-5 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={chiroLogo} alt={nl.app.logoAlt} className="h-10 w-auto" />
            <span>
              <span className="block font-bold font-display text-lg leading-tight tracking-tight">
                {nl.app.naam}
              </span>
              <span className="block font-semibold text-[11.5px] text-rood-donker leading-snug">
                {nl.app.organisatie}
              </span>
            </span>
          </Link>
          <nav className="flex items-center gap-4 font-medium text-sm sm:gap-7">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="pb-0.5 text-mist hover:text-inkt"
                activeProps={{
                  className: "border-b-2 border-rood font-semibold text-inkt",
                }}
                activeOptions={{ exact: item.to === "/" }}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-5 py-8">
        <Outlet />
      </main>

      <footer className="border-lijn border-t bg-zand/50">
        <div className="mx-auto flex max-w-5xl flex-wrap items-baseline justify-between gap-3 px-5 py-6 text-mist text-sm">
          <p>
            <span className="font-semibold text-inkt">{nl.footer.contactKop}</span>
            {" · "}
            <span className="font-mono text-[13px]">{nl.footer.email}</span>
            {" · "}
            {nl.footer.adres}
          </p>
          <p className="flex items-baseline gap-4 text-xs">
            {nl.footer.kleineLetters}
            {/* The door handle for leiding; renters scroll past a footer link. */}
            <Link to="/beheer" className="font-semibold text-mist hover:text-inkt">
              {nl.footer.beheer}
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
